import React, {useState} from 'react';
import {Tooltip} from '@mui/material';
import {Body, Button} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import type {OffPlatformCreatorSnapshot} from '@/api/fetchers/crm/offPlatformCreators';
import CreatorAction, {
  CREATOR_ACTION_KEYS,
} from '@/views/creators/components/creator-action/CreatorAction';
import {CreatorProps} from '@/views/creators/components/creator-action/action/ActionProps';
import useOffPlatformInviteDisabledReason from '@/views/creators/creator-crm/hooks/use-off-platform-invite-disabled-reason';
import {OFF_PLATFORM_SOURCE} from '@/views/creators/creator-crm/utils/offPlatformSource';
import styles from './OffPlatformProfile.module.scss';

export const OFF_PLATFORM_INVITE_TEST_IDS = {
  BUTTON: 'off-platform-invite-button',
  STATUS: 'off-platform-invite-status',
};

function OffPlatformInviteAction({
  creator,
}: {
  creator: OffPlatformCreatorSnapshot;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const {disabledReason, isLoading} =
    useOffPlatformInviteDisabledReason(creator);
  const actionCreators: CreatorProps[] = [
    {
      id: creator.id,
      name: creator.displayName || creator.email,
      avatar: creator.avatarUrl || '',
      source: OFF_PLATFORM_SOURCE,
    },
  ];
  const button = (
    <Button
      testID={OFF_PLATFORM_INVITE_TEST_IDS.BUTTON}
      appearance="neutral"
      mode="filled"
      size="medium"
      disabled={isLoading || !!disabledReason}
      onClick={() => setIsOpen(true)}
    >
      {translate('Invite to campaign')}
    </Button>
  );

  return (
    <div className={styles.inviteAction}>
      {disabledReason ? (
        <Tooltip title={disabledReason}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      {(creator.campaignInvites ?? []).map((invite) => (
        <Body
          key={invite.campaignId}
          size="sm"
          data-testid={OFF_PLATFORM_INVITE_TEST_IDS.STATUS}
        >
          {translate('Invited to {campaignName}', {
            campaignName: invite.campaignName,
          })}
        </Body>
      ))}
      {isOpen ? (
        <CreatorAction
          creators={actionCreators}
          activeCreatorAction={CREATOR_ACTION_KEYS.ADD_TO_CAMPAIGN}
          close={() => setIsOpen(false)}
        />
      ) : null}
    </div>
  );
}

export default OffPlatformInviteAction;
